import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormikConfig, FormikProvider, useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  selectCountries,
  selectCountriesWithInterner,
} from "src/redux/countries/selectors";
import { selectIndustries } from "src/redux/industries/selectors";
import {
  selectCurrentUser,
  selectIsLoadingUpdate,
} from "src/redux/currentUser/selector";
import { Roles } from "src/@types/users";
import { updateCurrentUserAsync } from "src/redux/currentUser/actions";
import { Button } from "src/components/Button";
import { scrollTop } from "src/utils/scrollTop";
import { ButtonVariants } from "src/components/Button/types";
import { getInitialValuesForSettings } from "./utils/getInitialValuesForSettings";
import { SettingItem } from "./SettingItem";
import {
  SETTINGS_VALIDATION_SCHEMA_FOR_AGENT,
  SETTINGS_VALIDATION_SCHEMA_OWNER,
} from "./constants/validation";
import {
  getSettingItemsForAgent,
  getSettingItemsForOwner,
} from "./constants/setting-items-by-role";
import { settignsTrns } from "./trns";
import { ISettignsValues } from "./types";

export const SettingsForm: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectCurrentUser);
  const isLoadingUpdate = useAppSelector(selectIsLoadingUpdate);
  const { data: industries } = useAppSelector(selectIndustries);
  const { items: countries } = useAppSelector(selectCountries);
  const { items: countriesWithInterner } = useAppSelector(
    selectCountriesWithInterner
  );

  const settingItems = useMemo(() => {
    const isAgent = user.host.role === Roles.AGENT;

    return isAgent
      ? getSettingItemsForAgent(countries)
      : getSettingItemsForOwner(industries, countries, countriesWithInterner);
  }, [industries, countries, countriesWithInterner, user.host.role]);

  const settingsValidationSchema = useMemo(() => {
    const isAgent = user.host.role === Roles.AGENT;

    return isAgent
      ? SETTINGS_VALIDATION_SCHEMA_FOR_AGENT
      : SETTINGS_VALIDATION_SCHEMA_OWNER;
  }, [user.host.role]);

  const formikProps: FormikConfig<ISettignsValues> = {
    initialValues: getInitialValuesForSettings(user),
    validationSchema: settingsValidationSchema,
    onSubmit: (values) => {
      if (!formik.dirty) return;

      dispatch(
        updateCurrentUserAsync({
          values,
          onSuccess: () => {
            scrollTop();
          },
        })
      );
    },
  };

  const formik = useFormik<ISettignsValues>(formikProps);

  return (
    <FormikProvider value={formik}>
      <Form className="flex flex-col gap-8 sm:gap-10">
        {settingItems.map((item) => (
          <SettingItem key={item.id} {...item} />
        ))}
        <Button
          type="submit"
          className="w-2/3 sm:w-1/2 mx-auto min-h-11"
          isLoading={isLoadingUpdate}
          variant={ButtonVariants.PRIMARY}
        >
          {t(settignsTrns.submitButton)}
        </Button>
      </Form>
    </FormikProvider>
  );
};
